"use client";

import { useState } from 'react';
import { Button } from './Button';
import Modal from './Modal';
import DegreeTargets, { type DegreeTarget } from '@/app/degree-planning/components/DegreeTargets';
import {
  OperationalDataRow,
  OperationalHeader,
  OperationalPage,
  OperationalResult,
  OperationalSection,
  OperationalSource,
  OperationalStatus,
  Surface,
} from './OperationalPage';
import type { CoreJourneyReleaseRoute } from '@/app/lib/coreJourneyReleaseFixture';
import ScheduleChangeProposalPanel from '@/app/scheduling/components/ScheduleChangeProposalPanel';
import type { ScheduleChangeProposal, ScheduleRevision } from '@/app/types/academicChangeSet';

type DialogId = 'filters' | 'search' | 'course-actions' | 'candidate' | 'plan' | 'degree-info' | null;

const fixtureDegreeTargets: DegreeTarget[] = [
  { ID: 198, Name: 'Computer Science', 'Degree Code': 'B.S.' },
  { ID: 640, Name: 'Mathematics', 'Degree Code': 'B.A.' },
];

const fixtureCopy = {
  explore: {
    eyebrow: 'Fall 2026 checked-in catalog fixture',
    title: 'Find a course',
    description: 'Narrow one deterministic catalog result and inspect its available handoffs.',
  },
  scheduling: {
    eyebrow: 'Fall 2026 · example data',
    title: 'Schedule',
    description: 'Review the draft before applying it.',
  },
  'degree-planning': {
    eyebrow: 'Deterministic plan fixture',
    title: 'Degree planning',
    description: 'Review a small illustrative plan without importing, calculating, or saving progress.',
  },
} as const;

export function CoreJourneyReleaseFixture({ route }: { route: CoreJourneyReleaseRoute }) {
  const [dialog, setDialog] = useState<DialogId>(null);
  const [scheduleProposalState, setScheduleProposalState] = useState<'pending' | 'applied' | 'dismissed'>('pending');
  const [degreeTargets, setDegreeTargets] = useState(fixtureDegreeTargets);
  const [selectedDegreeTarget, setSelectedDegreeTarget] = useState<DegreeTarget | null>(null);
  const copy = fixtureCopy[route];

  const scheduleProposal: ScheduleChangeProposal = {
    id: 'fixture-schedule-proposal-v2',
    schedule_id: 'fixture-schedule',
    owner_id: 'fixture-owner',
    base_revision_id: 'fixture-schedule-revision-2',
    status: 'pending',
    summary: 'Move Data Structures to an earlier Livingston section',
    operations: [{
      kind: 'replace_section',
      courseBaseId: '01:198:112',
      courseName: 'Data Structures',
      fromSectionId: '18842',
      toSectionId: '18837',
    }],
    preview_snapshot: { name: 'Fall schedule', term_id: 'fall-2026', is_public: false, classes: ['18837', '15521'], configuration: null },
    validation: { blockingIssues: [] },
    source: 'assistant',
    created_at: '2026-08-28T14:30:00Z',
    updated_at: '2026-08-28T14:30:00Z',
  };
  const scheduleRevisions: ScheduleRevision[] = [
    ...(scheduleProposalState === 'applied' ? [{
      id: 'fixture-schedule-revision-3', schedule_id: 'fixture-schedule', owner_id: 'fixture-owner',
      parent_revision_id: 'fixture-schedule-revision-2', sequence: 3,
      snapshot: scheduleProposal.preview_snapshot, operations: scheduleProposal.operations,
      summary: scheduleProposal.summary, source: 'assistant' as const, proposal_id: scheduleProposal.id,
      content_hash: '3'.repeat(32), created_at: '2026-08-28T14:31:00Z',
    }] : []),
    {
      id: 'fixture-schedule-revision-2', schedule_id: 'fixture-schedule', owner_id: 'fixture-owner',
      parent_revision_id: 'fixture-schedule-revision-1', sequence: 2,
      snapshot: { name: 'Fall schedule', term_id: 'fall-2026', is_public: false, classes: ['18842', '15521'], configuration: null },
      operations: [], summary: 'Added Calculus I', source: 'student', content_hash: '2'.repeat(32), created_at: '2026-08-28T13:10:00Z',
    },
    {
      id: 'fixture-schedule-revision-1', schedule_id: 'fixture-schedule', owner_id: 'fixture-owner', sequence: 1,
      snapshot: { name: 'Fall schedule', term_id: 'fall-2026', is_public: false, classes: ['18842'], configuration: null },
      operations: [], summary: 'Schedule created', source: 'student', content_hash: '1'.repeat(32), created_at: '2026-08-28T12:00:00Z',
    },
  ];

  return (
    <OperationalPage
      className={`px-4 pb-24 sm:px-6 lg:px-8 ${
        route === 'scheduling'
          ? 'min-h-screen pt-14'
          : route === 'degree-planning'
            ? 'min-h-full pt-20 sm:pt-24'
            : 'min-h-full pt-0 sm:pt-2'
      }`}
      data-audit-ready={`${route}-release-populated`}
      data-audit-state="populated"
      data-audit-owner="none"
    >
      <div className={`mx-auto max-w-6xl ${route === 'degree-planning' ? 'space-y-2' : 'space-y-3 sm:space-y-4'}`}>
        <OperationalHeader eyebrow={copy.eyebrow} title={copy.title} description={copy.description} />

        {route === 'explore' ? (
          <>
            <Surface className="p-2 sm:p-3">
              <OperationalSection title="Search the catalog" description="One checked-in result; no live section status is implied.">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Button className="scroll-mb-28" variant="secondary" onClick={() => setDialog('search')}>Search courses</Button>
                  <Button className="scroll-mb-28" variant="secondary" onClick={() => setDialog('filters')}>Course filters</Button>
                  <OperationalStatus tone="neutral">Fall 2026 · New Brunswick</OperationalStatus>
                </div>
              </OperationalSection>
            </Surface>
            <Surface className="p-2 sm:p-3">
              <OperationalSection title="1 checked-in result">
                <OperationalResult actions={<Button className="scroll-mb-28" onClick={() => setDialog('course-actions')}>Course actions</Button>}>
                  <p className="font-semibold text-[var(--ss-text)]">Introduction to Computer Science, 01:198:111</p>
                  <p className="mt-1 text-sm text-[var(--ss-text-muted)]">4 credits · Livingston example meeting · Tuesday 2:00–3:20 PM</p>
                  <p className="mt-2 text-sm text-[var(--ss-text-muted)]">The fixture does not claim that a section is currently open.</p>
                </OperationalResult>
              </OperationalSection>
            </Surface>
          </>
        ) : null}

        {route === 'scheduling' ? (
          <>
            <ScheduleChangeProposalPanel
              proposal={scheduleProposalState === 'pending' ? scheduleProposal : null}
              revisions={scheduleRevisions}
              onApply={() => setScheduleProposalState('applied')}
              onDismiss={() => setScheduleProposalState('dismissed')}
            />
            <Surface className="p-2 sm:p-3">
              <OperationalSection title="Schedule preview" description="Current and draft sections">
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-control border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] p-2">
                    <p className="text-xs font-semibold text-[var(--ss-text-muted)]">Tuesday</p>
                    {scheduleProposalState !== 'applied' ? <div className={`mt-10 rounded-control bg-red-700 p-2 text-white ${scheduleProposalState === 'pending' ? 'border-2 border-dashed border-red-400' : 'border-l-4 border-red-900'}`}>
                      {scheduleProposalState === 'pending' ? <span className="rounded bg-white px-1.5 py-0.5 text-[9px] font-black uppercase text-slate-900">Remove</span> : null}
                      <p className="mt-2 text-xs font-semibold">Data Structures</p><p className="text-[10px]">3:50–5:10 PM · 18842</p>
                    </div> : null}
                  </div>
                  <div className="rounded-control border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] p-2">
                    <p className="text-xs font-semibold text-[var(--ss-text-muted)]">Thursday</p>
                    {scheduleProposalState !== 'dismissed' ? <div className={`mt-3 rounded-control bg-emerald-700 p-2 text-white ${scheduleProposalState === 'pending' ? 'border-2 border-dashed border-emerald-300 ring-2 ring-emerald-500/30' : 'border-l-4 border-emerald-900'}`}>
                      {scheduleProposalState === 'pending' ? <span className="rounded bg-white px-1.5 py-0.5 text-[9px] font-black uppercase text-slate-900">Proposed add</span> : null}
                      <p className="mt-2 text-xs font-semibold">Data Structures</p><p className="text-[10px]">10:20–11:40 AM · 18837</p>
                    </div> : null}
                  </div>
                  <div className="rounded-control border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] p-2">
                    <p className="text-xs font-semibold text-[var(--ss-text-muted)]">Friday</p>
                    <div className="mt-16 rounded-control border-l-4 border-blue-700 bg-blue-600 p-2 text-white">
                      <p className="text-xs font-semibold">Calculus I</p><p className="text-[10px]">1:10–2:30 PM · unchanged</p>
                    </div>
                  </div>
                </div>
                <Button className="mt-3 scroll-mb-28" variant="secondary" onClick={() => setDialog('candidate')}>Review candidate</Button>
              </OperationalSection>
            </Surface>
          </>
        ) : null}

        {route === 'degree-planning' ? (
          <>
            <Surface className="p-2 sm:p-3">
              <div className="grid gap-2">
                <p className="px-1 text-sm font-semibold text-[var(--ss-text-muted)]">Degree targets</p>
                <DegreeTargets
                  targets={degreeTargets}
                  onView={(degree) => {
                    setSelectedDegreeTarget(degree);
                    setDialog('degree-info');
                  }}
                  onRemove={(degree) => setDegreeTargets((targets) => targets.filter((target) => target.ID !== degree.ID))}
                />
              </div>
            </Surface>
            <Surface className="p-2 sm:p-3">
              <OperationalSection title="Illustrative computer science plan" description="The fixture demonstrates review hierarchy, not degree completion." actions={<Button data-release-static-subject="degree-primary-action" className="scroll-mb-28" onClick={() => setDialog('plan')}>Review plan</Button>}>
                <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                  <div className="grid gap-2">
                    <OperationalDataRow label="Fall term" value="01:198:111" detail="Introduction to Computer Science · 4 credits" />
                    <OperationalDataRow label="Math option" value="01:640:151" detail="Calculus I · 4 credits" />
                    <OperationalDataRow label="Progress" value="Not evaluated" detail="No transcript or owner data is present" />
                  </div>
                </div>
              </OperationalSection>
            </Surface>
          </>
        ) : null}

        <Surface className="p-3">
          <OperationalSource
            kind="checked-in"
            source="Fall 2026 mini catalog"
            retrievedAt="release fixture contract 2026-07-31"
          />
        </Surface>
      </div>

      <Modal isOpen={dialog === 'search'} onClose={() => setDialog(null)} title="Search courses" size="sm">
        <label className="grid gap-2 text-sm font-semibold text-[var(--ss-text)]">
          Course code or title
          <input className="ss-field" defaultValue="computer science" aria-label="Course code or title" />
        </label>
        <p className="mt-3 text-sm text-[var(--ss-text-muted)]">The release fixture keeps the checked-in result fixed.</p>
      </Modal>
      <Modal isOpen={dialog === 'filters'} onClose={() => setDialog(null)} title="Course filters" size="sm">
        <label className="flex min-h-12 items-center gap-3 text-sm font-medium text-[var(--ss-text)]">
          <input type="checkbox" defaultChecked /> New Brunswick
        </label>
        <label className="flex min-h-12 items-center gap-3 text-sm font-medium text-[var(--ss-text)]">
          <input type="checkbox" /> Open sections only
        </label>
      </Modal>
      <Modal isOpen={dialog === 'course-actions'} onClose={() => setDialog(null)} title="Course actions" size="sm">
        <p className="text-sm text-[var(--ss-text-muted)]">Review the checked-in course identity before choosing a real scheduling, list, or Course Sniper handoff outside this fixture.</p>
        <Button className="mt-4" variant="secondary" onClick={() => setDialog(null)}>Done</Button>
      </Modal>
      <Modal isOpen={dialog === 'candidate'} onClose={() => setDialog(null)} title="Review candidate" size="md">
        <div className="grid gap-2">
          <OperationalDataRow label="Course count" value="2 illustrative courses" />
          <OperationalDataRow label="Conflict check" value="No claim" detail="The fixture does not run the schedule generator" />
          <OperationalDataRow label="Persistence" value="Disabled" detail="Nothing is written or assigned to an owner" />
        </div>
        <Button className="mt-4" variant="secondary" onClick={() => setDialog(null)}>Done</Button>
      </Modal>
      <Modal isOpen={dialog === 'plan'} onClose={() => setDialog(null)} title="Review plan" size="md">
        <div className="grid gap-2">
          <OperationalDataRow label="Requirement calculation" value="Not run" />
          <OperationalDataRow label="Transcript" value="Not attached" />
          <OperationalDataRow label="Persistence" value="Disabled" detail="This is not a saved degree plan" />
        </div>
        <Button className="mt-4" variant="secondary" onClick={() => setDialog(null)}>Done</Button>
      </Modal>
      <Modal
        isOpen={dialog === 'degree-info' && selectedDegreeTarget !== null}
        onClose={() => {
          setDialog(null);
          setSelectedDegreeTarget(null);
        }}
        title={selectedDegreeTarget ? `${selectedDegreeTarget.Name} degree information` : 'Degree information'}
        size="sm"
      >
        {selectedDegreeTarget ? (
          <div className="grid gap-2">
            <OperationalDataRow label="Program" value={selectedDegreeTarget.Name} />
            <OperationalDataRow label="Degree" value={selectedDegreeTarget['Degree Code'] ?? 'Not listed'} />
            <OperationalDataRow label="Fixture status" value="Illustrative only" detail="No degree catalog record is loaded or saved." />
          </div>
        ) : null}
      </Modal>
    </OperationalPage>
  );
}
